import { Modal, Table } from "@4uhub/lib4uhub";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import ResultsNonLabReceivedDetailsModal from "./ResultsNonLabReceivedDetailsModal";
import { IResSendNonLabWorklistZ } from "../../../../models/res-send-non-lab-worklist-z";
import { reprocessResSendNonLabWorklistZ } from "../../../../services/nonLabWorklistReprocess.service";
import { useNonLabReprocess } from "../NonLabShared/useNonLabReprocess";

const STATUS_ITEMS: Record<
  string,
  {
    color: "warning" | "info" | "success" | "error" | "primary" | "secondary";
    key: string;
  }
> = {
  DISPONIVEL: { color: "success", key: "available" },
  RECONHECIDO: { color: "info", key: "recognized" },
  "DOWNLOAD COMPLETO": { color: "primary", key: "downloaded" },
  INTEGRADO: { color: "secondary", key: "integrated" },
  ERRO: { color: "error", key: "error" },
  CANCELADO: { color: "warning", key: "cancelled" },
};

const RES_SEND_NON_LAB_WORKLIST_Z_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ResSendNonLabWorklistZ/ResSendNonLabWorklistZ";

const resSendNonLabWorklistZService = new MainApi<IResSendNonLabWorklistZ>(
  RES_SEND_NON_LAB_WORKLIST_Z_ROUTE,
);

const translationPath = "page.4uBus.logs.tasyToTasyResultsNonLabReceived.";

const ResultsNonLabReceived = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IResSendNonLabWorklistZ | null>(null);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    encounterNumber: "",
    IndividualDocumentOS: "",
    prescriptionNumber: "",
    prescriptionProcNumber: "",
    prescriptionNumberOS: "",
    prescriptionProcNumberOS: "",
    encounterNumberOS: "",
    status: "",
    createdStart: today,
    createdEnd: today,
  });

  const { t } = useTranslation();

  const columns: GridColDef<IResSendNonLabWorklistZ>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "encounterNumber",
      headerName: t(translationPath + "service_number") || "Service Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "individualDocumentOS",
      headerName: t(translationPath + "patient_cpf") || "Patient CPF",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "status",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          return (
            STATUS_ITEMS[status] || {
              color: "warning" as const,
              key: "pending",
            }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "prescriptionNumber",
      headerName:
        t(translationPath + "prescription_number") || "Prescription Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "prescriptionProcNumber",
      headerName:
        t(translationPath + "procedure_prescription_number") ||
        "Procedure Prescription Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "prescriptionNumberOS",
      headerName:
        t(translationPath + "external_prescription_number") ||
        "External Prescription Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "prescriptionProcNumberOS",
      headerName:
        t(translationPath + "external_procedure_prescription_number") ||
        "External Procedure Prescription Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "encounterNumberOS",
      headerName:
        t(translationPath + "external_service_number") ||
        "External Service Number",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
  ];

  const params = useMemo(
    () => ({
      refresh,
      ...filterParams,
      createdStart: filterParams?.createdStart
        ? new Date(filterParams.createdStart.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      createdEnd: filterParams?.createdEnd
        ? new Date(filterParams.createdEnd.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
    }),
    [refresh, filterParams],
  );

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const { reprocessing, reprocessRowAction, multipleRowsActions } =
    useNonLabReprocess({
      translationPath,
      domainName: "ressendnonlabworklistzlog",
      reprocess: reprocessResSendNonLabWorklistZ,
      onDone: onRefreshHandler,
    });

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <ResultsNonLabReceivedDetailsModal
            resultsNonLabReceivedData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Table<IResSendNonLabWorklistZ>
        title={t(translationPath + "title")}
        columns={columns}
        service={resSendNonLabWorklistZService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        syncing={reprocessing}
        getRowId={(row) => (row.identifier ? row.identifier : 0)}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          reprocessRowAction(params.row),
        ]}
        serviceProps={params}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default ResultsNonLabReceived;
